import React, { useCallback } from "react";
import SettingsForm from "../../components/Settings/Old/Form/SettingsForm";
import SettingsLabel from "../../components/Settings/Old/Form/SettingsLabel";
import {
    ControllerAPI,
    ExecutionErrorRO,
    ExecutionRO,
    ExecutionState,
    RoleRO,
    ScenarioRO,
    WorkerRO
} from "../../api/api";
import SettingsInfo from "../../components/Settings/Old/Form/SettingsInfo";
import { DateTime } from "luxon";
import { generateToString } from "../../util/StringUtils";
import ExecutionErrorTable from "./ExecutionErrorTable";
import ExecutionRoleTable from "./ExecutionRoleTable";
import QAButtonRow from "../../components/Design/Layout/QAButtonRow";
import QAButton from "../../components/Design/Controls/QAButton";
import { PlayArrow, Stop } from "@mui/icons-material";
import ConfirmationDialog from "../../components/Hooks/Dialog/ConfirmationDialog";
import { hasFailed } from "@miragon/react-utils-api";
import useDialog from "../../components/Hooks/Dialog/useDialog";
import useSnackbar from "../../components/Hooks/Snackbar/useSnackbar";
import { updateExecutionsManually } from "../../state/ExecutionsState";
import { useAppDispatch } from "../../state/Store";
import { makeStyles } from "@mui/styles";

interface Props {
    execution: ExecutionRO | undefined;
    executionErrors: ExecutionErrorRO[];
    scenarios: ScenarioRO[];
    workers: WorkerRO[];
    roles: RoleRO[];
}

const stateToString = generateToString<ExecutionState>({
    CREATED: "Erstellt",
    STARTED: "Gestartet",
    FINISHED: "Beendet"
});

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "row"
    },
    column: {
        flex: 1,
        marginRight: "1rem"
    }
});

const ExecutionInfo: React.FC<Props> = props => {
    const classes = useStyles();
    const dialog = useDialog();
    const snackbar = useSnackbar();
    const dispatch = useAppDispatch();

    const { execution, scenarios, roles, workers, executionErrors } = props;

    const scenario = scenarios.find(s => s.id === execution?.scenarioId);

    const onStart = useCallback(async () => {
        if (!execution) {
            return;
        }

        const confirmation = await dialog.open(ConfirmationDialog, {
            title: "Ausführung starten",
            question: "Wollen Sie die Ausführung wirklich starten?",
            acceptButtonTitle: "Starten"
        });

        if (!confirmation.accepted) {
            return;
        }

        const response = await ControllerAPI.startExecution({ executionId: execution.id });
        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateExecutionsManually({
                update: [response.result]
            }));
            snackbar.onSuccess("Ausführung gestartet.");
        }
    }, [dispatch, dialog, execution, snackbar]);

    const onFinish = useCallback(async () => {
        if (!execution) {
            return;
        }

        const confirmation = await dialog.open(ConfirmationDialog, {
            title: "Ausführung beenden",
            question: "Wollen Sie die Ausführung wirklich beenden?",
            acceptButtonTitle: "Beenden"
        });

        if (!confirmation.accepted) {
            return;
        }

        const response = await ControllerAPI.finishExecution({ executionId: execution.id });
        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateExecutionsManually({
                update: [response.result]
            }));
            snackbar.onSuccess("Ausführung beendet.");
        }
    }, [dispatch, dialog, execution, snackbar]);

    if (!execution) {
        return null;
    }

    return (
        <>

            <SettingsForm full>

                <SettingsLabel large>Allgemein</SettingsLabel>

                <div className={classes.container}>

                    <div className={classes.column}>

                        <SettingsInfo
                            label="Name"
                            value={execution.name} />

                        <SettingsInfo
                            label="Szenario"
                            value={scenario?.name ?? "Unbekannt"} />

                        <SettingsInfo
                            label="Erstellt"
                            value={DateTime.fromISO(execution.created).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)} />

                        <SettingsInfo
                            label="Gestartet"
                            value={execution.started ? DateTime.fromISO(execution.started).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS) : "-"} />

                        <SettingsInfo
                            label="Beendet"
                            value={execution.finished ? DateTime.fromISO(execution.finished).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS) : "-"} />

                    </div>

                    <div className={classes.column}>

                        <QAButtonRow marginBottom>

                            <QAButton
                                title="Starten"
                                onClicked={onStart}
                                iconLeft={<PlayArrow />}
                                disabled={execution.state !== "CREATED" && execution.state !== "FINISHED"} />

                            <QAButton
                                title="Beenden"
                                onClicked={onFinish}
                                iconLeft={<Stop />}
                                disabled={execution.state !== "STARTED"} />

                        </QAButtonRow>

                        <SettingsInfo
                            label="Ramp-Up-Intervall"
                            value={execution.rampUpPeriodMs === 0 ? "Keines" : (`${execution.rampUpPeriodMs}ms`)} />

                        <SettingsInfo
                            label="Status"
                            value={stateToString(execution.state) ?? "Unbekannt"} />

                    </div>

                </div>

                <SettingsLabel large>Rollen</SettingsLabel>

                <ExecutionRoleTable
                    executionId={execution.id}
                    availableRoles={roles}
                    workers={workers}
                    roles={execution.roles} />

                <SettingsLabel large>Fehler</SettingsLabel>

                <ExecutionErrorTable
                    errors={executionErrors.filter(e => e.executionId === execution.id)}
                    roles={roles} />

            </SettingsForm>

        </>
    );
};

export default ExecutionInfo;
